/**
 * A build var the app cannot work without. Read at module load, so a build that
 * lacks it stops on the first line of the bundle with a message naming the
 * variable, instead of calling a base nobody chose.
 */
function requiredEnv(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. This build cannot reach its backend without it: set ${name} in the ` +
      '.env.<mode> file the build uses (or .env.local for `pnpm dev`) and rebuild.',
    );
  }
  return value;
}

/**
 * Typed, centralized access to build-time env (injected by rspack DefinePlugin).
 * Never read import.meta.env.APP_* directly elsewhere — go through `env`.
 */
export const env = {
  appEnv: import.meta.env.APP_ENV || 'development',
  // Login/Register VScanner banner. It scans through the live API (see vscannerApiUrl), which
  // production reaches only through a same-origin proxy, so it is shown in dev builds only
  // unless APP_VSCANNER_BANNER=true.
  vscannerBanner: import.meta.env.APP_VSCANNER_BANNER === 'true',
  // Base of the VScanner scan API (POST /scan, GET /scan/progress SSE, GET /scan/report/:id).
  // Default is the same-origin dev proxy in rspack.config.ts (the API sends no CORS headers).
  vscannerApiUrl: import.meta.env.APP_VSCANNER_API_URL || '/vscanner-api',
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,

  authUrl: import.meta.env.APP_AUTH_URL,
  // DataCentral defaults to v4 (APP_DATACENTRAL_API_URL points at /api/v4).
  dataCentralApiUrl: import.meta.env.APP_DATACENTRAL_API_URL,
  serviceApiUrl: import.meta.env.APP_SERVICE_API_URL,
  serviceApiV3Url: import.meta.env.APP_SERVICE_API_V3_URL,
  // margarita-foundation (identity, organization, teams, projects, IAM) — the
  // foundation half split out of @mgmt. Base includes /api/v1. Optional: when
  // unset the foundation clients fall back to @mgmt v3, which still serves the
  // same endpoints, so a build without it behaves exactly as before.
  foundationApiUrl: import.meta.env.APP_FOUNDATION_API_URL,
  // Full URL of margarita-foundation `GET /system/maintenance` (public). Empty = no maintenance gate.
  // Independent of APP_FOUNDATION_API_URL: the gate works before any API is cut over to foundation.
  maintenanceUrl: import.meta.env.APP_MAINTENANCE_URL || '',
  // @usagez read API (golang/margarita/usagez) — called directly, JWT-scoped.
  // Serves per-service CDN + media (LMS/VMS) usage. Base is the bare host
  // (no version prefix); paths are /services/:id/... . Tenant/scope from the JWT.
  usagezApiUrl: import.meta.env.APP_USAGEZ_API_URL,
  // FastChannel (FAST playout) — service_uuid-scoped v2 API on a dedicated host.
  // `fastChannelApiUrl` is the control plane (…/api); `fastChannelUrl` is the
  // streaming/health host used for channel health checks.
  fastChannelApiUrl: import.meta.env.APP_FASTC_API_URL,
  fastChannelUrl: import.meta.env.APP_FASTC_URL,

  // Tiger (managed SSL / cert inventory) — @tiger controlplane, called directly
  // (same shape as fastChannel). Base includes /api/v1. JWT-scoped; tenant is
  // implicit from the org claim (no tenant/serviceId in any path). This is the
  // single cert inventory: list/upload/issue/renew/revoke/bundle.
  tigerApiUrl: import.meta.env.APP_TIGER_API_URL,

  // Compute (OpenNebula) — @compute controlplane, called directly, the same way
  // tiger and the DRM control plane are. Base includes /api/v3. The service is
  // still named per request through the X-Compute-Metadata-ID header, which is
  // the one thing it needs that a tenant claim does not carry; see computeApi.
  // Console loads the service's metadata id before issuing direct Compute
  // requests.
  //
  // MANDATORY: there is no @mgmt fallback any more. An unset or empty value
  // throws when this module loads (requiredEnv), naming the variable.
  computeApiUrl: requiredEnv('APP_COMPUTE_API_URL', import.meta.env.APP_COMPUTE_API_URL),

  // Managed Kubernetes v2 (margarita/kubernetes, Go) — multi-tenant service called
  // DIRECTLY, JWT-scoped. Base already includes /api/v2/kubernetes. Tenant is
  // implicit from the JWT org claim: no serviceId in any path (unlike the v1
  // s10015 console, which is service_uuid-scoped through the @mgmt gateway).
  k8sV2ApiUrl: import.meta.env.APP_K8S_API_URL,

  // Self-serve service activation, for local work.
  //
  // SELF_SERVE_ACTIVATION in pages/services/activation-mode.ts is off, so every
  // heavy service's activation CTA opens contact-sales instead of provisioning —
  // which is right in production and leaves an activation flow untestable
  // without impersonating somebody. Set this in .env.local and the button
  // activates.
  //
  // A variable rather than editing the constant, so testing an onboarding flow
  // does not need a source change that must be remembered not to commit.
  selfServeActivation: import.meta.env.APP_SELF_ACTIVATION === 'true',

  // Pre-experimental org allowlist (comma-separated org/customer IDs). Orgs here
  // get the pilot surfaces + self-serve activation of the heavy cloud services,
  // the same way an impersonating staff member does. Mirrors the backend
  // OPERATIONS_EXPERIMENTAL_ORGS; unset/empty = nobody is in the pilot.
  experimentalOrgs: (import.meta.env.APP_OPERATIONS_EXPERIMENTAL_ORGS || '')
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean),

  // Healthcheck & Alerting (monitoring) — dedicated Go service. Base already
  // includes /api/v1; JWT-scoped, watchpoints filtered by project_id.
  healthcheckApiUrl: import.meta.env.APP_HEALTHCHECK_API_URL,

  // Media Transcode Service — @media gateway (golang/margarita/media), called
  // directly like tiger/control. Base includes /api/v1; JWT-scoped, tenant
  // implicit from the org claim. Onboarding via /workspace; resources proxied
  // per-tenant under /tenant/* to the caller's bound vStream tenant.
  mediaApiUrl: import.meta.env.APP_MEDIA_API_URL,
  ravenApiUrl: import.meta.env.APP_RAVEN_API_URL,
  eagleApiUrl: import.meta.env.APP_EAGLE_API_URL,
  // WAAP read paths on margarita-flashcone (HMAC routes). Base includes /api/v1. Unset -> every WAAP
  // call goes through @mgmt (serviceApiV3) exactly as before; staging sets it, production leaves it empty.
  waapFlashconeApiUrl: import.meta.env.APP_WAAP_FLASHCONE_API_URL,

  // Business Mail — margarita/mail (vMail), called directly like compute and
  // media. Base includes /api/v3; JWT-scoped, organization from the token.
  // Activation via /workspace.
  mailApiUrl: import.meta.env.APP_MAIL_API_URL,

  // Hosting — margarita/weaver (CyberPanel through hosting-manager), called directly like mail.
  // Base includes /api/v1; JWT-scoped, organization from the token. Activation via /workspace.
  hostingApiUrl: import.meta.env.APP_HOSTING_API_URL,

  // AI Token — margarita/myna (new-api behind token.vnetwork.vn), called directly like hosting.
  // Base includes /api/v1; JWT-scoped, organization from the token. Activation via /workspace.
  tokenApiUrl: import.meta.env.APP_TOKEN_API_URL,

  // DRM control plane (sig-control) — multi-tenant Go service called directly,
  // JWT-scoped. Base already includes /api (streams/provision/usage). Tenant is
  // implicit from the JWT org claim — no tenant/serviceId in any path.
  controlApiUrl: import.meta.env.APP_CONTROL_API_URL,

  // AES/DRM gateway (zone endpoint) — the public host players/customer backends
  // call for /drm/signed + /drm/keyfile. A separate service from the control
  // plane; the console only renders its example URLs and a health pill.
  aesGatewayHost: import.meta.env.APP_AES_API_URL,

  // NOTE: the AES gateway ops surface (/logz, /metricsz, /usagez, /healthz) is
  // reached through the @mgmt control plane (serviceApiV3), which holds the
  // gateway ADMIN_API_KEY server-side. No browser-side AES URL/key here.

  keycloak: {
    url: import.meta.env.APP_KEYCLOAK_URL || 'https://id.vnetwork.dev',
    realm: import.meta.env.APP_KEYCLOAK_REALM || 'partner',
    clientId: import.meta.env.APP_KEYCLOAK_CLIENT_ID || 'partner-console',
  },

  // Google standalone sign-in (direct Google Identity Services, NOT via Keycloak).
  // Public OAuth Web client id; ships in the bundle by design. Empty = the
  // standalone Google button stays hidden (see GoogleSignInButton).
  google: {
    clientId: import.meta.env.APP_GOOGLE_CLIENT_ID || '',
  },

  // Firebase Analytics (web) = Google Analytics 4 under the hood. The web app
  // config is public by design (it ships in the client bundle); the values come
  // from Firebase console → Project settings → Your apps → Web app. Analytics
  // stays a no-op unless `measurementId` (G-XXXX) is present — see `analytics.ts`.
  firebase: {
    apiKey: import.meta.env.APP_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.APP_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.APP_FIREBASE_APP_ID || '',
    measurementId: import.meta.env.APP_FIREBASE_MEASUREMENT_ID || '',
  },

  // VMware WebMKS console tunnel — the base URL of the vSphere HTML5 web-console
  // proxy (same var as APP_SSH_TUNNEL_URL in @console). The console iframe is built
  // as `${webConsoleTunnelUrl}/ui/webconsole.html?vmId=...&session=...`.
  webConsoleTunnelUrl: import.meta.env.APP_SSH_TUNNEL_URL || '',

  // Product documentation (Docusaurus, docs.vnetwork.dev). Served as a static
  // SSG site with open CORS, so the in-app Documentation panel fetches a page's
  // rendered <article> and re-renders it natively with our tokens.
  docsUrl: import.meta.env.APP_DOCS_URL || 'https://docs.vnetwork.dev',

  // Terms of Service page of the marketing site (served from the client-prod
  // origin, not www). The sign-up and invitation dialogs fetch its rendered text
  // instead of bundling a copy, so the console always shows what is published.
  termsUrl:
    import.meta.env.APP_TERMS_URL ||
    'https://client-prod-vnetwork-v4.vnetwork.dev/en-US/terms-of-service/',

  // Ask AI / live chat. Bot replies come from the N8N webhook; when the bot
  // escalates, the panel connects to the VChat socket for a human agent.
  // Defaults mirror the @livechat widget so this works without extra config.
  chat: {
    chatbotUrl:
      import.meta.env.APP_CHATBOT_URL || 'https://n8n.vnetwork.dev/webhook/chatbot',
    socketUrl:
      import.meta.env.APP_CHAT_SOCKET_URL ||
      'https://appspot.staging.vchat.vnetwork.dev',
    socketPath: import.meta.env.APP_CHAT_SOCKET_PATH || '/event',
  },
} as const;

export type Env = typeof env;
